<template>
	<div class="flex w-full flex-col justify-start gap-2">
		<div ref="autocompleteEl" class="relative -mt-2 text-gray-900 dark:text-white">
			<label
				for="search"
				class="mb-0.5 whitespace-nowrap text-xs text-gray-600 dark:text-gray-100"
			>
				{{ label }}
			</label>
			<div
				class="h-9 w-full border border-solid border-gray-500 bg-white pl-9 pr-14 transition-all dark:bg-gray-800"
				:class="[
					showResultsContainer ? 'rounded-t' : 'rounded',
					{
						'focus-within:border-orange-500 dark:focus-within:border-orange-200':
							currentlyFocusedEl === searchInputEl,
					},
				]"
			>
				<input
					id="search"
					ref="searchInputEl"
					v-model="searchInputText"
					autocomplete="off"
					:placeholder="placeholder"
					type="text"
					class="peer flex h-8 w-full cursor-text border-0 text-sm outline-none placeholder:transition-opacity focus:ring-0 focus:placeholder:opacity-100 dark:placeholder-gray-200"
					:class="{
						'placeholder:opacity-0': !selections.length,
					}"
					role="combobox"
					aria-controls="results"
					:aria-expanded="showResultsContainer"
					aria-autocomplete="list"
					v-bind="{
						'aria-label': ariaLabel,
						'aria-describedby': ariaDescribedby,
					}"
					@keydown.down.prevent="focusResult(0)"
					@keydown.enter.prevent="selectFirstResult"
					@keydown.esc.prevent="showResultsContainer = false"
					@focusin="highlightFirstResult = true"
					@focusout="highlightFirstResult = false"
				/>
				<!-- sorry this is after the input but it has to be for 'peer-focus:' to work -->
				<FAIcon
					icon="fa-magnifying-glass"
					size=""
					class="absolute left-2 top-8 h-5 w-5 transition-colors peer-focus:text-orange-500 dark:peer-focus:text-orange-200"
				/>

				<div class="absolute right-2 top-7 flex h-7 items-center justify-center gap-2">
					<button
						v-if="showResultsToggle"
						class="items-center justify-center rounded-full outline-none ring-offset-2 transition focus:bg-gray-200 focus:ring-2 focus:ring-gray-800 dark:focus:bg-gray-600 dark:focus:ring-white dark:focus:ring-offset-gray-500"
						@click.prevent.stop="toggleResultsContainer"
					>
						<FAIcon
							:class="{ 'rotate-180': showResultsContainer }"
							class="duration-200"
							fixed-width
							icon="angle-down"
							size="lg"
						/>
					</button>
					<button
						v-if="(searchInputText && clearable) || (selections.length && multiple)"
						class="items-center justify-center rounded-full outline-none ring-offset-2 transition focus:bg-gray-200 focus:ring-2 focus:ring-gray-800 dark:focus:bg-gray-600 dark:focus:ring-white dark:focus:ring-offset-gray-500"
						@click="clearAutocomplete"
					>
						<FAIcon icon="fa-xmark" size="lg" />
					</button>
				</div>
				<div
					v-if="showResultsContainer"
					id="results"
					ref="resultsEl"
					class="absolute left-0 top-full z-50 flex max-h-96 w-full flex-col gap-1 overflow-clip overflow-y-auto !rounded-b border border-t-0 border-gray-500 bg-white p-2 drop-shadow-xl dark:border-white dark:bg-gray-900"
					role="listbox"
				>
					<LoadingSpinner v-if="loading" class="py-2" />
					<button
						v-for="(result, i) in results"
						v-else-if="results.length > 0"
						:key="i"
						ref="resultItems"
						class="rounded bg-white p-2 !no-underline outline-none ring-orange-600 hover:bg-gray-200 focus:bg-gray-200 focus:ring dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
						:class="{
							'first:bg-gray-200 dark:first:bg-gray-800': highlightFirstResult,
						}"
						role="option"
						:aria-selected="currentlyFocusedEl === resultItems[i]"
						@keydown.up.prevent="focusResult(i - 1)"
						@keydown.down.prevent="focusResult(i + 1)"
						@click.prevent="resultClicked(result)"
					>
						<slot name="item" v-bind="result">
							<span class="block text-left text-sm font-semibold">{{ result }}</span>
						</slot>
					</button>
					<slot v-else name="no-data">
						<span class="rounded bg-white py-2 pl-3 font-bold dark:bg-gray-800">
							No results
						</span>
					</slot>
				</div>
			</div>
		</div>
		<div v-if="multiple" class="flex w-full flex-wrap content-start gap-2">
			<template v-for="selection in selections">
				<slot name="selection" v-bind="{ selection }">
					{{ selection }}
				</slot>
			</template>
		</div>
	</div>
</template>
<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useActiveElement } from '@vueuse/core';

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue';

const emit = defineEmits(['update:search-input', 'change', 'click:clear', 'update:modelValue']);

const props = defineProps({
	ariaAutocomplete: { type: String, required: false, default: 'list' },
	ariaControls: { type: String, required: false, default: 'search' },
	ariaDescribedby: { type: String, required: false, default: 'search' },
	ariaExpanded: { type: Boolean, required: false, default: false },
	ariaLabel: { type: String, required: false, default: 'Search' },
	autofocus: { type: Boolean, required: false, default: false },
	clearable: { type: Boolean, required: false, default: false },
	closeOnContentClick: { type: Boolean, required: false, default: false },
	hideSelected: { type: Boolean, required: false, default: false },
	showResultsToggle: { type: Boolean, required: false, default: false },
	items: { type: Array, required: true },
	label: { type: String, required: true },
	loading: { type: Boolean, required: false, default: false },
	multiple: { type: Boolean, required: false, default: false },
	noFilter: { type: Boolean, required: false, default: false },
	placeholder: { type: String, required: false, default: 'Search' },
	modelValue: { type: [Object, Array], required: false, default: null },
});

const searchInputText = ref('');
const selections = ref([]);
const highlightFirstResult = ref(false);
const showResultsContainer = ref(false);

const autocompleteEl = ref();
const searchInputEl = ref();
const resultsEl = ref();
const resultItems = ref([]);
const currentlyFocusedEl = useActiveElement();

const results = computed(() => {
	if ((props.noFilter || !searchInputText.value) && props.hideSelected) {
		return props.items.filter(item => !selections.value.includes(item));
	}

	if (props.noFilter && !props.hideSelected) {
		return props.items;
	}

	if (!props.noFilter && props.hideSelected && searchInputText.value) {
		return props.items.filter(
			item =>
				!selections.value.includes(item) &&
				item.toLowerCase().includes(searchInputText.value.toLowerCase())
		);
	}

	return props.items.filter(item =>
		item.toLowerCase().includes(searchInputText.value.toLowerCase())
	);
});

const focusIsWithinAutocomplete = computed(() => {
	return autocompleteEl.value?.contains(currentlyFocusedEl.value);
});

async function toggleResultsContainer() {
	if (showResultsContainer.value) {
		searchInputEl.value.focus();
	}

	showResultsContainer.value = !showResultsContainer.value;
}
async function focusResult(i) {
	if (i < 0 || !(results.value.length > 0)) {
		searchInputEl.value.focus();
		return;
	}

	if (results.value.length > 0 && searchInputText.value?.trim() === '') {
		showResultsContainer.value = true;
	}

	if (showResultsContainer.value && i < results.value.length) {
		await nextTick();
		resultItems.value[i].focus();
	}
}

async function resultClicked(result) {
	if (typeof result === 'string' && [null, undefined, ''].includes(result?.trim())) {
		return;
	}
	emit('change', result);
	if (props.multiple) {
		searchInputText.value = '';
		selections.value = [...selections.value, result];
	}
	if (props.closeOnContentClick) {
		setTimeout(() => {
			currentlyFocusedEl.value.blur();
			showResultsContainer.value = false;
			searchInputEl.value.focus();
		}, 1);
	}
	searchInputEl.value.focus();
}

function selectFirstResult() {
	if (results.value.length > 0) {
		if (showResultsContainer.value) {
			resultClicked(results.value[0]);
			showResultsContainer.value = false;
		} else {
			showResultsContainer.value = true;
		}
	}
}

function clearAutocomplete() {
	searchInputText.value = '';
	selections.value = [];
	searchInputEl.value.focus();
	showResultsContainer.value = props.autofocus;
	emit('click:clear');
}

watch(
	() => props.modelValue,
	() => {
		if (props.multiple && props.modelValue !== selections.value) {
			selections.value = props.modelValue;
			searchInputEl.value?.focus();
		}
	},
	{ immediate: true }
);

watch(focusIsWithinAutocomplete, newVal => {
	if (!newVal) {
		showResultsContainer.value = false;
	}
});

watch(searchInputText, () => {
	if (searchInputText.value.trim()) {
		showResultsContainer.value = true;
		emit('update:search-input', searchInputText.value);
	}
});

watch(
	selections,
	() => {
		emit('update:modelValue', selections.value);
	},
	{ deep: true }
);

onMounted(() => {
	if (props.autofocus) {
		searchInputEl.value.focus();
	}
});

defineExpose({
	searchInputText,
	results,
	selections,
	resultClicked,
	toggleResultsContainer,
});
</script>
